import { Capacitor } from "@capacitor/core";

/**
 * A fake `Capacitor.Plugins.UXCam`, enabled by `REACT_APP_MOCK_UXCAM=true`.
 *
 * Session replay is otherwise invisible outside a native build: the real plugin
 * only exists on device, so every UXCam helper no-ops on web and in Cypress.
 * That is exactly why REFSIX-776 shipped with recording broken for
 * already-logged-in users — there was no way to observe the call sequence
 * without a device, a UXCam account, and a session that survived sampling.
 *
 * With this installed the whole lifecycle is observable in the browser
 * (`pnpm run phone:local`) and assertable in Cypress. Mirrors the existing
 * MockWatch (`REACT_APP_MOCK_WATCH_SERVICE`) and refsix-iap/mock-stores
 * patterns: a local stand-in for a vendor we can't drive in tests.
 *
 * It records calls only — it deliberately does NOT try to reproduce UXCam's
 * screen capture or occlusion. It can prove *we* call the right things at the
 * right times and that the occlusion class is on the right elements; only a
 * real replay can prove UXCam honours it.
 */

export interface MockUXCamCall {
    method: string;
    args?: unknown;
}

export interface MockUXCamApi {
    /** Every plugin call this page load, in order. */
    calls: MockUXCamCall[];
    /** How many sessions have been started — a logout should make this go up. */
    sessionCount: number;
    recording: boolean;
    /** Identity attached to the current session, if any. */
    identity?: string;
    /** Screen names tagged this page load, in order. */
    screens: string[];
    sessionUrl: string;
    /** Names of methods called, in order — convenient for sequence assertions. */
    methods: () => string[];
    /** Outline everything carrying the occlusion class, to eyeball coverage. */
    highlightOcclusions: (on?: boolean) => void;
    reset: () => void;
}

const HIGHLIGHT_STYLE_ID = "uxcam-mock-occlusion-highlight";

function installHighlightStyles(on: boolean) {
    const existing = document.getElementById(HIGHLIGHT_STYLE_ID);
    if (!on) {
        existing?.remove();
        return;
    }
    if (existing) return;

    const style = document.createElement("style");
    style.id = HIGHLIGHT_STYLE_ID;
    style.textContent = `
        .uxcam-occlude {
            outline: 2px dashed #ff00aa !important;
            outline-offset: 2px;
            background-color: rgba(255, 0, 170, 0.12) !important;
        }
    `;
    document.head.appendChild(style);
}

export function createMockUXCam(): MockUXCamApi & Record<string, unknown> {
    const state: {
        calls: MockUXCamCall[];
        sessionCount: number;
        recording: boolean;
        identity?: string;
        screens: string[];
        sessionUrl: string;
    } = {
        calls: [],
        sessionCount: 0,
        recording: false,
        identity: undefined,
        screens: [],
        sessionUrl: "",
    };

    const record = (method: string, args?: unknown) => {
        state.calls.push(args === undefined ? { method } : { method, args });
        console.log(`[mock UXCam] ${method}`, args ?? "");
    };

    const mock = {
        // Marks this as the stand-in so getUXCamStatus can say so out loud —
        // "recording: YES" on a web build must not be mistaken for the real thing.
        __isMock: true,

        async optIntoSchematicRecordings() {
            record("optIntoSchematicRecordings");
        },

        async startWithConfiguration(options: { configuration: unknown }) {
            record("startWithConfiguration", options?.configuration);
            state.sessionCount += 1;
            state.recording = true;
            // A new session gets a new identity — the previous user's must not
            // carry over (that was the logout attribution bug).
            state.identity = undefined;
            state.sessionUrl = `https://mock.uxcam.local/session/${state.sessionCount}`;
            return { sessionUrl: state.sessionUrl };
        },

        async stopSessionAndUploadData() {
            record("stopSessionAndUploadData");
            state.recording = false;
            state.sessionUrl = "";
        },

        async setUserIdentity(options: { identity: string }) {
            record("setUserIdentity", options?.identity);
            state.identity = options?.identity;
        },

        async tagScreenName(options: { screenName: string }) {
            record("tagScreenName", options?.screenName);
            state.screens.push(options?.screenName);
        },

        async isRecording() {
            return { isRecording: state.recording };
        },

        async urlForCurrentSession() {
            return { url: state.sessionUrl };
        },

        get calls() {
            return state.calls;
        },
        get sessionCount() {
            return state.sessionCount;
        },
        get recording() {
            return state.recording;
        },
        get identity() {
            return state.identity;
        },
        get screens() {
            return state.screens;
        },
        get sessionUrl() {
            return state.sessionUrl;
        },
        methods: () => state.calls.map((call) => call.method),
        highlightOcclusions: (on: boolean = true) => installHighlightStyles(on),
        reset: () => {
            state.calls = [];
            state.sessionCount = 0;
            state.recording = false;
            state.identity = undefined;
            state.screens = [];
            state.sessionUrl = "";
        },
    };

    return mock as unknown as MockUXCamApi & Record<string, unknown>;
}

/**
 * Register the mock at `Capacitor.Plugins.UXCam` and expose it on
 * `window.__uxcamMock` for the console and Cypress. No-op unless
 * `REACT_APP_MOCK_UXCAM=true`, and never replaces a real native plugin.
 *
 * Must run before the first startUXCam() — AppInit calls it ahead of
 * initUXCamSession.
 */
export function installMockUXCam(): MockUXCamApi | undefined {
    if (process.env.REACT_APP_MOCK_UXCAM !== "true") return undefined;

    // Never on a device build: the flag is set for the local/tim/anup envs,
    // which also produce native builds, and the Plugins.UXCam check below
    // only protects us if native registration wins the race against module
    // import. On native, the real plugin always wins.
    if (Capacitor.isNativePlatform()) return undefined;

    const capacitor = Capacitor as unknown as {
        Plugins?: Record<string, unknown>;
    };
    if (!capacitor.Plugins) capacitor.Plugins = {};
    if (capacitor.Plugins.UXCam) {
        // A native build: the real plugin always wins.
        return undefined;
    }

    const mock = createMockUXCam();
    capacitor.Plugins.UXCam = mock;
    (window as unknown as { __uxcamMock?: unknown }).__uxcamMock = mock;
    console.log(
        "[mock UXCam] installed — inspect window.__uxcamMock, " +
            "or run window.__uxcamMock.highlightOcclusions() to outline masked fields"
    );
    return mock;
}
